import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  UserCheck, Search, Users, Clock, Shield, AlertTriangle, 
  Eye, XCircle, Building2, Mail, Calendar, Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ImpersonationManager Component
 * 
 * Super Admin tool for:
 * - Starting impersonation sessions
 * - Viewing active sessions
 * - Viewing session history/audit logs
 * - Terminating sessions for security
 */
export default function ImpersonationManager() {
  const [users, setUsers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [impersonationReason, setImpersonationReason] = useState('');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    loadData();
  }, [searchTerm, roleFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load users and sessions in parallel
      const [usersRes, sessionsRes, activeRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/impersonatable-users`, {
          params: { search: searchTerm, role: roleFilter, limit: 50 }
        }),
        axios.get(`${API_URL}/api/admin/impersonation-sessions`, {
          params: { limit: 50 }
        }),
        axios.get(`${API_URL}/api/admin/impersonation-sessions`, {
          params: { active_only: true }
        })
      ]);
      
      setUsers(usersRes.data.users || []);
      setSessions(sessionsRes.data.sessions || []);
      setActiveSessions(activeRes.data.sessions || []);
    } catch (error) {
      console.error('Error loading impersonation data:', error);
      toast.error('Error loading impersonation data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartImpersonation = (user) => {
    setSelectedUser(user);
    setImpersonationReason('');
    setShowConfirmDialog(true);
  };

  const confirmStartImpersonation = async () => {
    if (!selectedUser) return;
    
    setStarting(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/impersonate`, {
        user_id: selectedUser.id,
        reason: impersonationReason || 'Technical support'
      });
      
      if (response.data.success) {
        // Store impersonation data
        localStorage.setItem('impersonation_data', JSON.stringify({
          session_id: response.data.session_id,
          original_admin_id: response.data.original_admin_id,
          impersonated_user: response.data.impersonated_user,
          expires_at: response.data.expires_at
        }));
        
        // Store the original admin's token for later
        const currentToken = localStorage.getItem('token');
        const currentUser = localStorage.getItem('user');
        localStorage.setItem('original_admin_token', currentToken);
        localStorage.setItem('original_admin_user', currentUser);
        
        // Set new impersonation token
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.impersonated_user));
        
        // Set clinic_id if applicable
        if (response.data.impersonated_user?.clinic_id) {
          localStorage.setItem('clinic_id', response.data.impersonated_user.clinic_id);
        }
        
        toast.success(response.data.message);
        
        // Redirect based on role - must match the normal login redirect logic
        const role = response.data.impersonated_user?.role;
        if (role === 'patient') {
          window.location.href = '/patient';
        } else if (['admin', 'clinic_admin', 'manager'].includes(role)) {
          // Only admin, clinic_admin, manager go to /admin
          window.location.href = '/admin';
        } else if (['staff', 'receptionist', 'nurse', 'provider'].includes(role)) {
          // Staff roles including receptionist go to /staff
          window.location.href = '/staff';
        } else {
          window.location.href = '/';
        }
      }
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast.error(error.response?.data?.detail || 'Error starting impersonation');
    } finally {
      setStarting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleTerminateSession = async (sessionId) => {
    if (!confirm('Are you sure you want to forcefully terminate this impersonation session?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/impersonation-session/${sessionId}/terminate`);
      toast.success('Session terminated successfully');
      loadData();
    } catch (error) {
      console.error('Error terminating session:', error);
      toast.error(error.response?.data?.detail || 'Error terminating session');
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      'admin': 'bg-purple-100 text-purple-800',
      'clinic_admin': 'bg-purple-100 text-purple-800',
      'manager': 'bg-blue-100 text-blue-800',
      'staff': 'bg-green-100 text-green-800',
      'nurse': 'bg-teal-100 text-teal-800',
      'provider': 'bg-indigo-100 text-indigo-800',
      'patient': 'bg-gray-100 text-gray-800',
      'receptionist': 'bg-orange-100 text-orange-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
          <UserCheck className="h-8 w-8 text-teal-600" />
          Impersonation Management
        </h1>
        <p className="text-gray-600">
          Impersonate users for technical support. All sessions are audited.
        </p>
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-3 mb-6">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Active ({activeSessions.length})
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Available Users to Impersonate</CardTitle>
              <CardDescription>
                Select a user to start an impersonation session
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="impersonation-search"
                  />
                </div>
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md min-w-[150px]"
                  data-testid="impersonation-role-filter"
                >
                  <option value="">All roles</option>
                  <option value="admin">Clinic Admin</option>
                  <option value="manager">Manager</option>
                  <option value="staff">Staff</option>
                  <option value="patient">Patient</option>
                </select>
              </div>

              {/* Users List */}
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading users...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <div 
                      key={user.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid={`user-row-${user.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                          {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{user.full_name || 'No name'}</span>
                            <Badge className={getRoleBadgeColor(user.role)}>
                              {user.role}
                            </Badge>
                            {!user.is_active && (
                              <Badge variant="outline" className="text-red-600 border-red-300">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </span>
                            {user.clinic_name && (
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {user.clinic_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => handleStartImpersonation(user)}
                        variant="outline"
                        className="flex items-center gap-2 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
                        data-testid={`impersonate-btn-${user.id}`}
                      >
                        <Eye className="h-4 w-4" />
                        Impersonate
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Active Sessions Tab */}
        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                Active Sessions
              </CardTitle>
              <CardDescription>
                Impersonation sessions currently in progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              {activeSessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No active impersonation sessions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activeSessions.map((session) => (
                    <div 
                      key={session.id}
                      className="p-4 border border-amber-200 bg-amber-50 rounded-lg"
                      data-testid={`active-session-${session.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-green-100 text-green-800">Active</Badge>
                            <span className="text-sm text-gray-500">
                              Duration: {session.duration}
                            </span>
                          </div>
                          <p className="font-medium">
                            {session.original_admin_name} ({session.original_admin_email})
                          </p>
                          <p className="text-sm text-gray-600">
                            → Impersonating: <strong>{session.impersonated_user_name}</strong> ({session.impersonated_user_email})
                          </p>
                          {session.reason && (
                            <p className="text-sm text-gray-500 mt-1">
                              Reason: {session.reason}
                            </p>
                          )}
                        </div>
                        <Button
                          onClick={() => handleTerminateSession(session.id)}
                          variant="destructive"
                          size="sm"
                          className="flex items-center gap-1"
                          data-testid={`terminate-session-${session.id}`}
                        >
                          <XCircle className="h-4 w-4" />
                          Terminate
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Session History
              </CardTitle>
              <CardDescription>
                Audit log of all impersonation sessions
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sessions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No session history</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session) => (
                    <div 
                      key={session.id}
                      className={`p-4 border rounded-lg ${session.is_active ? 'border-amber-200 bg-amber-50' : 'bg-white'}`}
                      data-testid={`session-history-${session.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={session.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                              {session.is_active ? 'Active' : 'Ended'}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {session.duration}
                            </span>
                            {session.end_reason && session.end_reason.includes('force') && (
                              <Badge variant="outline" className="text-red-600 border-red-300">
                                Forcefully terminated
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-gray-500">Original Admin:</p>
                              <p className="font-medium">{session.original_admin_name}</p>
                              <p className="text-gray-500 text-xs">{session.original_admin_email}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Impersonated User:</p>
                              <p className="font-medium">{session.impersonated_user_name}</p>
                              <p className="text-gray-500 text-xs">{session.impersonated_user_email} ({session.impersonated_user_role})</p>
                            </div>
                          </div>
                          
                          <div className="mt-2 text-xs text-gray-500 flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              Start: {new Date(session.started_at).toLocaleString()}
                            </span>
                            {session.ended_at && (
                              <span>
                                End: {new Date(session.ended_at).toLocaleString()}
                              </span>
                            )}
                          </div>
                          
                          {session.reason && (
                            <p className="text-xs text-gray-500 mt-1">
                              Reason: {session.reason}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm Impersonation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Impersonation
            </DialogTitle>
            <DialogDescription>
              You are about to impersonate another user. This action will be logged in the audit trail.
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="font-semibold text-gray-900 mb-2">
                  User to impersonate:
                </p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>• Name: <strong>{selectedUser.full_name}</strong></p>
                  <p>• Email: <strong>{selectedUser.email}</strong></p>
                  <p>• Role: <strong>{selectedUser.role}</strong></p>
                  {selectedUser.clinic_name && (
                    <p>• Clinic: <strong>{selectedUser.clinic_name}</strong></p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Reason for impersonation (optional):
                </label>
                <Input
                  value={impersonationReason}
                  onChange={(e) => setImpersonationReason(e.target.value)}
                  placeholder="E.g.: Technical support, bug verification..."
                  data-testid="impersonation-reason-input"
                />
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                <p className="font-medium mb-1">⚠️ Important:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>You will have full access as this user</li>
                  <li>Session expires automatically in 2 hours</li>
                  <li>All actions will be recorded in the audit log</li>
                  <li>You can end the session at any time from the top banner</li>
                </ul>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowConfirmDialog(false)}
              disabled={starting}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmStartImpersonation}
              disabled={starting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-impersonation-btn"
            >
              {starting ? 'Starting...' : 'Start Impersonation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
